import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { Formik, Form } from "formik";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useAuthStore } from "../hooks";
import { ChangePasswordSchema } from "../schemas";
import { PATHS } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/common";

const ChangePasswordPage = observer(() => {
  const navigate = useNavigate();
  const { t } = useTran();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { changePasswordForm, loading, changePassword } = useAuthStore();

  const handleSubmit = async (
    values,
    { setErrors, setSubmitting, setTouched },
  ) => {
    const success = await changePassword(values);
    setSubmitting(false);

    if (success) {
      navigate(PATHS.DASHBOARD, { replace: true });
      return;
    }

    if (
      changePasswordForm?.errors &&
      Object.keys(changePasswordForm.errors).length > 0
    ) {
      setErrors({ ...changePasswordForm.errors });
      setTouched(
        { currentPassword: true, newPassword: true, confirmPassword: true },
        true,
      );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md overflow-hidden rounded-radius bg-card p-8 shadow-2xl">
        <Formik
          initialValues={{
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={ChangePasswordSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched, values, handleChange }) => (
            <Form className="flex flex-col">
              <div className="mb-6 flex items-center justify-center">
                <Lock className="mr-2 h-8 w-8 text-primary" />
                <h1 className="text-2xl font-semibold text-primary">
                  {t("Change Password")}
                </h1>
              </div>

              <p className="mb-6 text-center text-sm text-muted-foreground">
                {t("Please enter your current password and choose a new one")}
              </p>

              <FormField
                name="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                placeholder={t("Current Password")}
                value={values.currentPassword}
                onChange={handleChange}
                error={touched.currentPassword && errors.currentPassword}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                }
                className="mb-4"
              />

              <FormField
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                placeholder={t("New Password")}
                value={values.newPassword}
                onChange={handleChange}
                error={touched.newPassword && errors.newPassword}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                }
                className="mb-4"
              />

              <FormField
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder={t("Confirm new password")}
                value={values.confirmPassword}
                onChange={handleChange}
                error={touched.confirmPassword && errors.confirmPassword}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                }
                className="mb-4"
              />

              {changePasswordForm?.serverError && (
                <p className="mb-4 text-center text-sm text-red-600">
                  {changePasswordForm.serverError}
                </p>
              )}

              <Button
                type="submit"
                disabled={isSubmitting || loading}
                className="w-full rounded-full bg-primary hover:bg-primary/90"
              >
                {isSubmitting || loading
                  ? t("Changing...")
                  : t("Change Password")}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(PATHS.DASHBOARD)}
                className="mt-4 w-full"
              >
                {t("Cancel")}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
});

export default ChangePasswordPage;
